// State coordinator for the Withdrawal Rules V2 editor.
//
// This hook is where all the moving pieces of the toggle editor come
// together:
//
//   - `useGetActiveRules`     — loads the currently active graph
//   - `graphToToggleState`    — hydrates local editing state from it
//   - React `useState`        — local mutations before publish
//   - `toggleStateToGraph`    — live validation on every edit
//   - `usePublishRules`       — POST when the admin clicks Publish
//
// Kept out of RulesTab.jsx because:
//   1. RulesTab is already >500 lines; adding hydration + dirty
//      tracking + validation + publish orchestration would push it
//      past the "reviewable in 15 minutes" bar.
//   2. Unit-testing the state machine is dramatically simpler
//      against a hook than against a mounted component tree with
//      React Query providers.
//
// Contract (returned object):
//
//   state          — current toggle-editor state (never null after
//                    the initial load succeeds; `undefined` while
//                    the GET is pending)
//   setTier1       — updater accepting a partial Tier-1 patch
//   setTier2       — updater accepting a partial Tier-2 patch
//   isLoading      — true while the initial GET is in flight
//   loadError      — non-null when the GET failed (excluding 404,
//                    which is the empty state)
//   readOnly       — true when the fetched graph can't be represented
//                    by the toggle UI; the caller should show a
//                    banner and disable every input
//   readOnlyReason — populated when readOnly is true, for the banner
//   validation     — result of the live forward-mapping; `{ ok: true,
//                    body }` when publishable, `{ ok: false, errors }`
//                    otherwise
//   isDirty        — true when `state` differs from the last-hydrated
//                    snapshot; Publish should only be reachable when
//                    this is true and `validation.ok`
//   publish        — click handler for the Publish button
//   publishState   — status of the ongoing mutation ("idle" | "pending"
//                    | "success" | "error")
//   publishError   — the raw AxiosError when publishState === "error"

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AxiosError } from "axios";

import { useGetActiveRules } from "../../../../queries/withdrawal-rules-v2/useGetActiveRules";
import { usePublishRules } from "../../../../queries/withdrawal-rules-v2/usePublishRules";
import {
  TIER_1_RULE_IDS,
  TIER_2_RULE_IDS,
  graphToToggleState,
  toggleStateToGraph,
  type DeserializeReason,
  type SerializeResult,
  type Tier1RuleState,
  type Tier2RuleState,
  type ToggleState,
} from "../../../../queries/withdrawal-rules-v2/mappers";
import type {
  ApiErrorEnvelope,
  RuleGraph,
} from "../../../../queries/withdrawal-rules-v2/types";

/**
 * UI default shown before ANYTHING is published (BE 404 → graph is
 * `null`). Deliberately different from the mapper's `emptyState()`:
 *
 *   - `emptyState()`   = "no enabled rules" — the invariant the
 *                        reverse mapper produces when it decodes a
 *                        published graph. Every rule `enabled:
 *                        false`.
 *   - `defaultUiState` = "everything visible, nothing filled in" —
 *                        preserves the pre-persistence UX where the
 *                        admin sees all available rules on first
 *                        open. Publish stays disabled until the
 *                        admin either fills the fields or turns
 *                        rules off, because live validation flags
 *                        every empty numeric input.
 *
 * The distinction is UI-only: once the admin publishes anything,
 * the hydration path takes over and the toggle grid reflects the
 * actual graph (which never contains a rule with an empty
 * numeric field).
 */
/**
 * Fresh sub-state for a single Tier-1 rule, with `enabled` set to
 * the requested value and every other field back to its
 * `defaultUiState()` value.
 *
 * Used when the admin disables a rule via its per-row toggle — the
 * inputs reset so re-enabling shows a blank slate rather than the
 * abandoned draft. Sourcing from `defaultUiState()` guarantees the
 * defaults never drift out of sync between initial mount and post-
 * disable resets.
 */
function defaultTier1RuleFor<K extends keyof Tier1RuleState>(
  ruleId: K,
  enabled: boolean,
): Tier1RuleState[K] {
  return {
    ...defaultUiState().tier1[ruleId],
    enabled,
  } as Tier1RuleState[K];
}

function defaultTier2RuleFor<K extends keyof Tier2RuleState>(
  ruleId: K,
  enabled: boolean,
): Tier2RuleState[K] {
  return {
    ...defaultUiState().tier2[ruleId],
    enabled,
  } as Tier2RuleState[K];
}

function defaultUiState(
  isDepositPaymentInstrumentRuleEnabled = true,
): ToggleState {
  return {
    tier1: {
      [TIER_1_RULE_IDS.MAX_AMOUNT]: { enabled: true, amount: "" },
      [TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL]: { enabled: true },
      [TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY]: {
        enabled: true,
        threshold: "",
        days: "",
      },
      [TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT]: {
        enabled: true,
        withdrawals: "",
      },
      [TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT]: {
        enabled: true,
        multiplier: "",
      },
    },
    tier2: {
      [TIER_2_RULE_IDS.REVIEWED_X_TIMES]: {
        enabled: true,
        reviews: "",
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.VIP_MATCHING]: {
        enabled: true,
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES]: {
        enabled: true,
        uses: "",
        within: "",
        period: "DAY",
      },
      [TIER_2_RULE_IDS.MIN_ACCOUNT_AGE]: { enabled: true, days: "" },
      [TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK]: {
        enabled: isDepositPaymentInstrumentRuleEnabled,
        lookback: "90d",
      },
    },
  };
}

// ─── Internal state shape ───────────────────────────────────────────

interface HydratedState {
  readonly state: ToggleState;
  /**
   * Snapshot of the state right after the last successful hydration
   * or publish. Kept alongside `state` so dirty detection is a cheap
   * string compare — see `computeDirty` below.
   */
  readonly baseline: ToggleState;
  readonly readOnly: false;
}

interface ReadOnlyState {
  readonly readOnly: true;
  readonly reason: DeserializeReason;
  readonly detail: string;
}

type EditorState = HydratedState | ReadOnlyState | null;

/**
 * Cheap deep-equality check tuned to our state shape (flat,
 * primitives only, no cycles). `JSON.stringify` is deterministic
 * for plain objects with the same key order, and our state is built
 * from typed literals so key order is guaranteed stable.
 */
function computeDirty(state: ToggleState, baseline: ToggleState): boolean {
  return JSON.stringify(state) !== JSON.stringify(baseline);
}

/**
 * True iff at least one Tier-1 rule is currently enabled. The full
 * editor invariant is "no Tier-2 rule may be on while Tier 1 has
 * none on" — this helper is the single source of truth every guard
 * checks against.
 */
function anyTier1Enabled(tier1: Tier1RuleState): boolean {
  return Object.values(tier1).some((rule) => !!rule.enabled);
}

/**
 * Force every Tier-2 rule's `enabled` flag off while preserving the
 * rest of each rule's sub-state (numeric inputs, period selections).
 *
 * Called from the Tier-1 mutations whenever an edit leaves Tier 1
 * with zero enabled rules — the invariant "Tier 2 can only be on
 * when Tier 1 has ≥1 on" requires a cascade so the state never
 * transiently violates it. Field preservation matches the
 * `toggleTier2All(false)` semantics: mass disables model "temporarily
 * hide" rather than "throw away my draft".
 *
 * Bails out early (returns the same reference) when no Tier-2 rule
 * is enabled — the React `setState` reducer above uses `===` for
 * short-circuit re-renders, so returning the input unchanged is
 * important, not cosmetic.
 */
function cascadeTier2Off(tier2: Tier2RuleState): Tier2RuleState {
  const dirty = Object.values(tier2).some((rule) => rule.enabled);
  if (!dirty) return tier2;
  return Object.fromEntries(
    Object.entries(tier2).map(([id, rule]) => [
      id,
      { ...(rule as object), enabled: false },
    ]),
  ) as unknown as Tier2RuleState;
}

/**
 * Turn a fetched `RuleGraph | null` from React Query into an
 * `EditorState`. `null` means "nothing published yet" → start with
 * the empty scaffold. A successful reverse-mapper decode gives us
 * the hydrated state. A failed decode drops the editor into
 * read-only mode.
 */
function hydrate(
  graph: RuleGraph | null,
  isDepositPaymentInstrumentRuleEnabled = true,
): EditorState {
  if (graph === null) {
    // No published version — admin gets the discovery-oriented
    // default (all toggles visible, nothing filled). See the note
    // on `defaultUiState()` for why this differs from the mapper's
    // `emptyState()`.
    const base = defaultUiState(isDepositPaymentInstrumentRuleEnabled);
    return {
      readOnly: false,
      state: base,
      baseline: base,
    };
  }
  const decoded = graphToToggleState(graph);
  if (!decoded.ok) {
    return { readOnly: true, reason: decoded.reason, detail: decoded.detail };
  }
  return {
    readOnly: false,
    state: decoded.state,
    baseline: decoded.state,
  };
}

// ─── Public return type ────────────────────────────────────────────

export type PublishState = "idle" | "pending" | "success" | "error";

export interface UseRulesEditorStateResult {
  readonly state: ToggleState | undefined;
  // Full-replacement of one rule's sub-state. Preferred in tests
  // where the entire shape is trivially typed; RulesTab uses the
  // partial-merge helpers below.
  readonly resetTier1Rule: <K extends keyof Tier1RuleState>(
    ruleId: K,
    next: Tier1RuleState[K],
  ) => void;
  readonly resetTier2Rule: <K extends keyof Tier2RuleState>(
    ruleId: K,
    next: Tier2RuleState[K],
  ) => void;
  // Partial patch merged into an existing rule. Mirrors the
  // pre-refactor RulesTab surface — every input `onChange` handler
  // calls `updateTier1Rule(id, { amount: e.target.value })`, so
  // preserving that shape keeps the wiring diff small.
  readonly updateTier1Rule: <K extends keyof Tier1RuleState>(
    ruleId: K,
    patch: Partial<Tier1RuleState[K]>,
  ) => void;
  readonly updateTier2Rule: <K extends keyof Tier2RuleState>(
    ruleId: K,
    patch: Partial<Tier2RuleState[K]>,
  ) => void;
  // Flip every rule in a tier on/off in a single click. Backs the
  // "All on" master switch in each RulePanel.
  readonly toggleTier1All: (enabled: boolean) => void;
  readonly toggleTier2All: (enabled: boolean) => void;
  readonly isLoading: boolean;
  readonly loadError: unknown;
  readonly readOnly: boolean;
  readonly readOnlyReason: string | null;
  /**
   * Escape hatch out of read-only mode: drops the un-decodable
   * graph baseline and puts the editor into the same "nothing
   * published yet" state the BE-404 path yields. The admin can then
   * configure and publish, which will overwrite the offending
   * server-side graph on the next `POST /rules`.
   *
   * No-op when the editor is not in read-only mode.
   */
  readonly dismissReadOnly: () => void;
  /**
   * Discards every unpublished edit and snaps the state back to
   * `baseline` — the snapshot taken at the last successful hydration
   * or publish. `isDirty` becomes `false` on the next render.
   *
   * No-op in three cases so the action is safe to wire behind a
   * button without extra guards:
   *   - editor not yet loaded (`state === undefined`)
   *   - editor in read-only mode (nothing to cancel)
   *   - state already equals baseline (`isDirty === false`)
   *
   * Deliberately does NOT go through `usePublishRules`'s cache — a
   * cancel is purely local; the last-hydrated baseline is authoritative
   * until the admin publishes again.
   */
  readonly cancel: () => void;
  readonly validation: SerializeResult | null;
  readonly isDirty: boolean;
  /**
   * True when the Tier-2 panel must not accept new "enable" input:
   * no Tier-1 rule is currently enabled, and the cross-tier
   * invariant forbids activating Tier 2 in that state. Consumed by
   * `RulesTab` to render the panel with disabled toggles + a
   * dedicated lock message; the reducer above enforces the same
   * rule for every mutation path so programmatic callers can't slip
   * past the UI guard.
   */
  readonly isTier2Locked: boolean;
  readonly publish: () => void;
  readonly publishState: PublishState;
  readonly publishError: AxiosError<ApiErrorEnvelope> | null;
}

// ─── Hook ──────────────────────────────────────────────────────────

export interface UseRulesEditorStateOptions {
  readonly isDepositPaymentInstrumentRuleEnabled?: boolean;
}

export function useRulesEditorState(
  options: UseRulesEditorStateOptions = {},
): UseRulesEditorStateResult {
  const { isDepositPaymentInstrumentRuleEnabled = true } = options;
  const activeRulesQuery = useGetActiveRules();
  const publishMutation = usePublishRules();

  // The local editor state is derived from the query but managed
  // separately so admin edits don't blow away on background refetch.
  // `null` = we have not seen a successful GET yet.
  const [editorState, setEditorState] = useState<EditorState>(null);

  // Re-hydrate whenever the GET resolves (or re-resolves after a
  // publish invalidates the cache and it refetches).
  //
  // Tracking `data` identity with a ref (rather than depending on
  // it in the effect's deps array) lets us gate re-hydration to
  // "the query produced a NEW data object". React Query returns a
  // stable reference across renders while the cache is fresh, so
  // this naturally fires once per fetch cycle.
  //
  // Note on the "admin has unsaved edits during a background
  // refetch" case: we currently always clobber local edits when a
  // new data reference arrives. In practice this only happens
  // after the admin's own publish (invalidation), where the reset
  // is desired. If that assumption breaks we'd add a "stash local
  // edits over refetch" flag — but let's not build for a bug we
  // don't have.
  const lastHydratedRef = useRef<RuleGraph | null | undefined>(undefined);
  const lastHydratedPaymentInstrumentFlagRef = useRef<boolean | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!activeRulesQuery.isSuccess) return;
    const data = activeRulesQuery.data ?? null;
    if (
      lastHydratedRef.current === data &&
      lastHydratedPaymentInstrumentFlagRef.current ===
        isDepositPaymentInstrumentRuleEnabled
    ) {
      return;
    }
    lastHydratedRef.current = data;
    lastHydratedPaymentInstrumentFlagRef.current =
      isDepositPaymentInstrumentRuleEnabled;
    setEditorState(hydrate(data, isDepositPaymentInstrumentRuleEnabled));
  }, [
    activeRulesQuery.data,
    activeRulesQuery.isSuccess,
    isDepositPaymentInstrumentRuleEnabled,
  ]);

  // Convenience for replacing a single rule's entire sub-state.
  const resetTier1Rule = useCallback(
    <K extends keyof Tier1RuleState>(ruleId: K, next: Tier1RuleState[K]) => {
      setEditorState((prev) => {
        if (!prev || prev.readOnly) return prev;
        return {
          ...prev,
          state: {
            ...prev.state,
            tier1: { ...prev.state.tier1, [ruleId]: next },
          },
        };
      });
    },
    [],
  );

  const resetTier2Rule = useCallback(
    <K extends keyof Tier2RuleState>(ruleId: K, next: Tier2RuleState[K]) => {
      setEditorState((prev) => {
        if (!prev || prev.readOnly) return prev;
        // Cross-tier invariant: a Tier-2 rule may never be `enabled`
        // when Tier 1 has zero enabled rules. Coerce the replacement
        // to `enabled: false` in that case rather than refusing the
        // call — the reset is still useful for resyncing the row's
        // other fields, and coercing lets callers stay agnostic of
        // the invariant.
        const unavailablePaymentInstrumentRule =
          ruleId ===
            TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK &&
          !isDepositPaymentInstrumentRuleEnabled;
        const coerced =
          (unavailablePaymentInstrumentRule ||
            !anyTier1Enabled(prev.state.tier1)) &&
          (next as { enabled: boolean }).enabled
            ? ({ ...(next as object), enabled: false } as Tier2RuleState[K])
            : next;
        return {
          ...prev,
          state: {
            ...prev.state,
            tier2: { ...prev.state.tier2, [ruleId]: coerced },
          },
        };
      });
    },
    [isDepositPaymentInstrumentRuleEnabled],
  );

  // Partial-patch helpers — the shape RulesTab's onChange handlers
  // have always used. Applies a shallow merge over the existing
  // sub-state and preserves keys the patch omits.
  //
  // Special case: an explicit `{ enabled: false }` patch wipes the
  // rest of the sub-state back to defaults. Rationale — this call
  // path is only reached when the admin flips the per-row toggle
  // OFF, and the UX is "disable resets the fields" so re-enabling
  // shows a fresh row rather than the abandoned draft. The tier
  // master toggle (`toggleTier{1,2}All` below) deliberately does
  // NOT reset — mass disable/enable preserves values because the
  // admin's intent there is "temporarily hide the whole tier", not
  // "throw away my drafts". That asymmetry is documented in both
  // functions so a future refactor doesn't accidentally unify them.
  const updateTier1Rule = useCallback(
    <K extends keyof Tier1RuleState>(
      ruleId: K,
      patch: Partial<Tier1RuleState[K]>,
    ) => {
      setEditorState((prev) => {
        if (!prev || prev.readOnly) return prev;
        const current = prev.state.tier1[ruleId];
        const shouldResetToDefaults =
          "enabled" in patch && patch.enabled === false;
        const nextRule = shouldResetToDefaults
          ? defaultTier1RuleFor(ruleId, false)
          : ({ ...current, ...patch } as Tier1RuleState[K]);
        const nextTier1 = { ...prev.state.tier1, [ruleId]: nextRule };
        // Cross-tier cascade: if this edit left Tier 1 with nothing
        // enabled, force Tier 2 off too so the invariant "T2 on ⇒ T1
        // has ≥1 on" holds after every reducer transition. Field
        // values in Tier 2 are preserved (see `cascadeTier2Off`).
        const nextTier2 = anyTier1Enabled(nextTier1)
          ? prev.state.tier2
          : cascadeTier2Off(prev.state.tier2);
        return {
          ...prev,
          state: {
            ...prev.state,
            tier1: nextTier1,
            tier2: nextTier2,
          },
        };
      });
    },
    [],
  );

  const updateTier2Rule = useCallback(
    <K extends keyof Tier2RuleState>(
      ruleId: K,
      patch: Partial<Tier2RuleState[K]>,
    ) => {
      setEditorState((prev) => {
        if (!prev || prev.readOnly) return prev;
        // Guard: while Tier 1 has zero enabled rules, no Tier-2 rule
        // may be flipped on. The Tier-2 panel is rendered locked so
        // the UI can't produce this patch, but callers (tests,
        // future imperative helpers) go through the same reducer —
        // enforce it here rather than trust the caller.
        const wouldEnable = "enabled" in patch && patch.enabled === true;
        if (
          wouldEnable &&
          ruleId ===
            TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK &&
          !isDepositPaymentInstrumentRuleEnabled
        ) {
          return prev;
        }
        if (wouldEnable && !anyTier1Enabled(prev.state.tier1)) {
          return prev;
        }
        const current = prev.state.tier2[ruleId];
        const shouldResetToDefaults =
          "enabled" in patch && patch.enabled === false;
        const nextRule = shouldResetToDefaults
          ? defaultTier2RuleFor(ruleId, false)
          : ({ ...current, ...patch } as Tier2RuleState[K]);
        return {
          ...prev,
          state: {
            ...prev.state,
            tier2: {
              ...prev.state.tier2,
              [ruleId]: nextRule,
            },
          },
        };
      });
    },
    [isDepositPaymentInstrumentRuleEnabled],
  );

  // Master switch: sets `enabled` on every rule in a tier at once
  // WITHOUT touching the rules' other fields. That way, un-toggling
  // and re-toggling doesn't lose values the admin already typed in.
  //
  // Intentional asymmetry with `updateTier1Rule({ enabled: false })`:
  // per-row disable resets the row's fields to defaults, master
  // disable preserves them. The two toggles model different admin
  // intents — "I don't want this rule" vs. "hide the whole tier for
  // a moment" — so they map to different state transitions.
  const toggleTier1All = useCallback((enabled: boolean) => {
    setEditorState((prev) => {
      if (!prev || prev.readOnly) return prev;
      // Object.fromEntries loses the strict per-key typing of
      // Tier1RuleState (which is a discriminated record, not a
      // homogeneous Record<string, T>). `as unknown as Tier1RuleState`
      // is the narrowest cast that reflects the runtime invariant.
      const nextTier1 = Object.fromEntries(
        Object.entries(prev.state.tier1).map(([id, rule]) => [
          id,
          { ...(rule as object), enabled },
        ]),
      ) as unknown as Tier1RuleState;
      // Turning all of Tier 1 off must cascade Tier 2 to off (same
      // invariant enforced by `updateTier1Rule`). Turning Tier 1 on
      // never touches Tier 2 — the admin still opts in per-row.
      const nextTier2 = enabled ? prev.state.tier2 : cascadeTier2Off(prev.state.tier2);
      return {
        ...prev,
        state: { ...prev.state, tier1: nextTier1, tier2: nextTier2 },
      };
    });
  }, []);

  const toggleTier2All = useCallback((enabled: boolean) => {
    setEditorState((prev) => {
      if (!prev || prev.readOnly) return prev;
      // Cross-tier guard: bulk-enabling Tier 2 requires Tier 1 to
      // already have ≥1 enabled rule. Bulk-disable is always allowed.
      if (enabled && !anyTier1Enabled(prev.state.tier1)) return prev;
      const nextTier2 = Object.fromEntries(
        Object.entries(prev.state.tier2).map(([id, rule]) => [
          id,
          {
            ...(rule as object),
            enabled:
              id ===
                TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK &&
              !isDepositPaymentInstrumentRuleEnabled
                ? false
                : enabled,
          },
        ]),
      ) as unknown as Tier2RuleState;
      return { ...prev, state: { ...prev.state, tier2: nextTier2 } };
    });
  }, [isDepositPaymentInstrumentRuleEnabled]);

  const cancel = useCallback(() => {
    setEditorState((prev) => {
      if (!prev) return prev;
      if (prev.readOnly) return prev;
      if (prev.state === prev.baseline) return prev;
      return { ...prev, state: prev.baseline };
    });
  }, []);

  const dismissReadOnly = useCallback(() => {
    setEditorState((prev) => {
      // Only meaningful from read-only. From any other state this is
      // a no-op, both to keep the API idempotent and to prevent the
      // banner button from clobbering an in-progress edit if the
      // caller wires it up wrong.
      if (!prev || !prev.readOnly) return prev;
      const base = defaultUiState(isDepositPaymentInstrumentRuleEnabled);
      return {
        readOnly: false,
        state: base,
        baseline: base,
      };
    });
    // We're intentionally NOT resetting `lastHydratedRef` here. If
    // a background refetch resolves with the same problematic graph
    // reference, the guard against re-hydration keeps the editor in
    // the fresh state the admin just opted into. Only a genuinely
    // new server response (e.g. after the admin's publish
    // succeeds) will trigger re-hydration.
  }, [isDepositPaymentInstrumentRuleEnabled]);

  // ─── Derived reads ──────────────────────────────────────────────

  const state = editorState && !editorState.readOnly ? editorState.state : undefined;

  const validation = useMemo<SerializeResult | null>(() => {
    if (!state) return null;
    return toggleStateToGraph(state);
  }, [state]);

  const isDirty = useMemo(() => {
    if (!editorState || editorState.readOnly) return false;
    return computeDirty(editorState.state, editorState.baseline);
  }, [editorState]);

  // Derived (not stored): the panel is locked whenever Tier 1 has
  // zero enabled rules. False while the editor hasn't hydrated yet
  // so the loading placeholder isn't accidentally treated as
  // "locked" — the panel isn't mounted at that point anyway, but
  // returning `false` keeps the contract simple.
  const isTier2Locked = useMemo(() => {
    if (!state) return false;
    return !anyTier1Enabled(state.tier1);
  }, [state]);

  const publishStateValue: PublishState = publishMutation.isPending
    ? "pending"
    : publishMutation.isError
      ? "error"
      : publishMutation.isSuccess
        ? "success"
        : "idle";

  // ─── Publish action ─────────────────────────────────────────────

  const publish = useCallback(() => {
    if (!editorState || editorState.readOnly) return;
    if (!validation || !validation.ok) return;
    publishMutation.mutate(validation.body, {
      onSuccess: () => {
        // Reset the baseline to the just-published snapshot so
        // `isDirty` flips back to `false` immediately. The React
        // Query cache invalidation is orthogonal — that will
        // eventually re-run `hydrate`, but doing this locally
        // avoids a "briefly dirty" flicker between mutation
        // success and the refetch settling.
        setEditorState((prev) => {
          if (!prev || prev.readOnly) return prev;
          return { ...prev, baseline: prev.state };
        });
      },
    });
  }, [editorState, validation, publishMutation]);

  return {
    state,
    resetTier1Rule,
    resetTier2Rule,
    updateTier1Rule,
    updateTier2Rule,
    toggleTier1All,
    toggleTier2All,
    cancel,
    dismissReadOnly,
    isLoading: activeRulesQuery.isPending,
    loadError: activeRulesQuery.error ?? null,
    readOnly: editorState?.readOnly === true,
    readOnlyReason:
      editorState?.readOnly === true
        ? `${editorState.reason}: ${editorState.detail}`
        : null,
    validation,
    isDirty,
    isTier2Locked,
    publish,
    publishState: publishStateValue,
    publishError: (publishMutation.error as AxiosError<ApiErrorEnvelope> | null) ?? null,
  };
}
